import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { databaseApi, type ChecklistDatabaseRow } from '../api/database'
import DataTable, { type Column, type SortDir } from '../components/DataTable'
import Button from '../components/Button'
import { Download, FileText, FileSpreadsheet, Columns3, Check, X, Bot, User } from 'lucide-react'

const PAGE_SIZE = 25
const DEFAULT_FIELDS = ['Title', 'EV_source', 'Nomenclature', 'PMID']
const STORAGE_KEY = 'ev-database-columns'

// Always-on system columns rendered outside the field picker.
const FILE_ID_KEY = '__file_id'
const SUBMITTED_KEY = '__submitted'

function loadStoredColumns(): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string') : null
  } catch {
    return null
  }
}

function fieldValue(row: ChecklistDatabaseRow, key: string): string {
  if (key === FILE_ID_KEY) return row.file_id
  if (key === SUBMITTED_KEY) return row.submitted_at || ''
  const v = row.fields[key]
  if (v == null) return ''
  return Array.isArray(v) ? v.join('; ') : String(v)
}

// Render PMID/DOI identifiers as outbound links (matches the Shiny database).
function renderIdentifier(raw: string) {
  const id = raw.trim()
  if (!id) return '—'
  const pmidMatch = id.match(/^(?:PMID)?(\d{6,9})$/)
  if (pmidMatch) {
    return (
      <a
        href={`https://pubmed.ncbi.nlm.nih.gov/${pmidMatch[1]}/`}
        target="_blank"
        rel="noreferrer"
        className="text-brand-600 hover:underline"
      >
        {id}
      </a>
    )
  }
  if (/^10\.\d/.test(id)) {
    return (
      <a href={`https://doi.org/${id}`} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
        {id}
      </a>
    )
  }
  return id
}

function escapeCsv(value: unknown): string {
  const str = value == null ? '' : String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function DatabasePage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [source, setSource] = useState<'all' | 'human' | 'ai'>('all')
  const [sortKey, setSortKey] = useState<string | null>(FILE_ID_KEY)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [selectedFields, setSelectedFields] = useState<string[]>(() => loadStoredColumns() ?? DEFAULT_FIELDS)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerSearch, setPickerSearch] = useState('')
  const pickerRef = useRef<HTMLDivElement | null>(null)

  // Fetch published checklists; source filter is applied by the backend.
  const { data, isLoading } = useQuery({
    queryKey: ['database-all', source],
    queryFn: () =>
      databaseApi.list({ page: 1, page_size: 5000, published_only: true, source }),
  })

  const allRows = data?.rows ?? []
  const labels = data?.column_labels ?? {}

  // Persist column selection.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFields))
    } catch {
      /* ignore */
    }
  }, [selectedFields])

  // Debounce the global search box.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 250)
    return () => window.clearTimeout(t)
  }, [search])

  // Close the column picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return
    const onClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [pickerOpen])

  // All field keys present across published checklists, sorted by friendly label.
  const availableFields = useMemo(() => {
    const keys = new Set<string>()
    for (const row of allRows) {
      for (const k of Object.keys(row.fields)) keys.add(k)
    }
    // Always offer the default fields even if a value is briefly missing.
    for (const k of DEFAULT_FIELDS) keys.add(k)
    keys.delete('Published') // implied — every row here is published
    return Array.from(keys).sort((a, b) =>
      (labels[a] || a).localeCompare(labels[b] || b, undefined, { sensitivity: 'base' })
    )
  }, [allRows, labels])

  const labelFor = (key: string) => {
    if (key === FILE_ID_KEY) return 'File ID'
    if (key === SUBMITTED_KEY) return 'Submitted'
    return labels[key] || key
  }

  const toggleField = (key: string) => {
    setSelectedFields((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  // Search + sort + paginate, all client-side.
  const processed = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase()
    const searchKeys = [FILE_ID_KEY, ...selectedFields]
    let rows = allRows
    if (term) {
      rows = rows.filter((row) => searchKeys.some((k) => fieldValue(row, k).toLowerCase().includes(term)))
    }
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => {
        const av = fieldValue(a, sortKey)
        const bv = fieldValue(b, sortKey)
        // Empty values sort last regardless of direction.
        if (av === '' && bv !== '') return 1
        if (bv === '' && av !== '') return -1
        const num = Number(av) - Number(bv)
        if (av !== '' && bv !== '' && !Number.isNaN(num)) return num * dir
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir
      })
    }
    return rows
  }, [allRows, debouncedSearch, selectedFields, sortKey, sortDir])

  const pageRows = useMemo(
    () => processed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [processed, page]
  )

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const columns = useMemo<Column<ChecklistDatabaseRow>[]>(() => {
    const cols: Column<ChecklistDatabaseRow>[] = [
      {
        key: FILE_ID_KEY,
        header: 'File ID',
        sortable: true,
        cell: (row) => (
          <div className="flex items-center gap-2">
            {row.is_ai_submitted ? (
              <span title="AI auto-submitted">
                <Bot className="w-4 h-4 text-purple-600" />
              </span>
            ) : (
              <span title="Human submitted">
                <User className="w-4 h-4 text-gray-500" />
              </span>
            )}
            <span className="font-mono font-medium">{row.file_id}</span>
          </div>
        ),
      },
    ]
    for (const key of selectedFields) {
      cols.push({
        key,
        header: labelFor(key),
        sortable: true,
        cell: (row) => {
          const val = fieldValue(row, key)
          if (!val) return <span className="text-gray-400">—</span>
          if (key === 'PMID' || key === 'DOI') return renderIdentifier(val)
          return (
            <span className="line-clamp-2" title={val}>
              {val}
            </span>
          )
        },
      })
    }
    cols.push({
      key: 'actions',
      header: 'Reports',
      className: 'whitespace-nowrap',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <a
            href={`/api/reports/${row.file_id}/html`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 hover:underline"
          >
            <FileText className="w-4 h-4" />
            HTML
          </a>
          <a
            href={`/api/reports/${row.file_id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 hover:underline"
          >
            <Download className="w-4 h-4" />
            PDF
          </a>
        </div>
      ),
    })
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields, labels])

  const exportCsv = () => {
    const keys = [FILE_ID_KEY, ...selectedFields]
    const headers = keys.map(labelFor)
    const lines = [headers.map(escapeCsv).join(',')]
    for (const row of processed) {
      lines.push(keys.map((k) => escapeCsv(fieldValue(row, k))).join(','))
    }
    downloadCsv(lines.join('\n'), 'ev-checklist-database.csv')
  }

  const filteredPickerFields = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase()
    if (!q) return availableFields
    return availableFields.filter((k) => labelFor(k).toLowerCase().includes(q) || k.toLowerCase().includes(q))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFields, pickerSearch, labels])

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">EV Checklist Database</h2>
        <p className="text-gray-600 mt-1">
          Browse all published EV research checklists. Use the search box to find studies, choose which columns to
          display, sort by any column, and download reports.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {/* Column picker */}
          <div className="relative" ref={pickerRef}>
          <Button variant="secondary" size="sm" onClick={() => setPickerOpen((o) => !o)}>
            <Columns3 className="w-4 h-4 mr-1" />
            Columns ({selectedFields.length})
          </Button>
          {pickerOpen && (
            <div className="absolute z-20 mt-2 w-80 bg-white border rounded-xl shadow-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">Show columns</span>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2 mb-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedFields(availableFields)}>
                  Select all
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedFields(DEFAULT_FIELDS)}>
                  Reset to default
                </Button>
              </div>
              <input
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Filter columns…"
                className="w-full mb-2 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="max-h-72 overflow-y-auto pr-1">
                {filteredPickerFields.map((key) => {
                  const checked = selectedFields.includes(key)
                  return (
                    <label
                      key={key}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <span
                        className={`flex items-center justify-center w-4 h-4 rounded border ${
                          checked ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-300'
                        }`}
                      >
                        {checked && <Check className="w-3 h-3" />}
                      </span>
                      <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleField(key)} />
                      <span className="text-sm text-gray-700">{labelFor(key)}</span>
                    </label>
                  )
                })}
                {filteredPickerFields.length === 0 && (
                  <p className="text-sm text-gray-400 px-2 py-2">No matching columns.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Source filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Source</label>
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value as 'all' | 'human' | 'ai')
              setPage(1)
            }}
            className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">All</option>
            <option value="human">Human only</option>
            <option value="ai">AI only</option>
          </select>
        </div>
      </div>

      <Button variant="secondary" size="sm" onClick={exportCsv} disabled={processed.length === 0}>
        <FileSpreadsheet className="w-4 h-4 mr-1" />
        Export CSV ({processed.length})
      </Button>
    </div>

      <DataTable
        columns={columns}
        rows={pageRows}
        keyExtractor={(row) => row.file_id}
        page={page}
        pageSize={PAGE_SIZE}
        total={processed.length}
        onPageChange={setPage}
        isLoading={isLoading}
        searchable
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search across displayed columns…"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        emptyMessage="No published checklists found."
      />
    </div>
  )
}
